import React, { useEffect, useState, useRef } from "react";
import dayjs from 'dayjs';
import "bootstrap";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Navbar } from "../Navigation/Navbar";
import {
  Typography, Button
} from "antd";
import "../../Styles/styles-StatusTracker.css";
import { useSelector, useDispatch } from "react-redux";
import { PlayerCard } from "./PlayerCard";
import { PlayerHeader } from "./PlayerHeader";
import { StatusTrackerCell } from "./StatusTrackerCell";
import { Table, Select, DatePicker, Pagination, Alert } from 'antd';
import { getStatusTrackerData, setFilter, getAvailableStatuses } from '../../store/actions/statustracker.actions'
import { formattedPlayerName } from "../../Utils/name_utils";
import moment from "moment";
import {
  getPlayerGPSData,
  setPlayerFilter,
} from "../../store/actions/player.actions";

import { StatusTrackerTutorial } from "./StatusTrackerTutorial";
import { HelpButton } from "../Common/HelpButton";

const { Text } = Typography;

export const StatusTracker = (props) => {
  const dispatch = useDispatch();
  const {
    players,
    positions,
    playerGroups,
    info: {
      display_name: teamName,
    }
  } = useSelector(({ team }) => team);

  const {
    loading,
    trackerData,
    filters: {
      playersFilter,
      positionsFilter,
      playerGroupsFilter,
      selectedMonth,
      playerStatuesFilter
    },
    statuses
  } = useSelector(({ statusTracker }) => statusTracker);

  const noDataStatusObj = [{ status_id: 9999, name: "No Data", color: "#f2f2f2"}];
  const headerRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [dateHeaders, setDateHeaders] = useState();
  const [uniqueKeys, setUniqueKeys] = useState([]);
  const [columns, setColumns] = useState();
  const [trackerDataSource, setTrackerDataSource] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [headerHeight, setHeaderHeight] = useState(0);
  const pageSize = 50;

  const [showTutorial, setShowTutorial] = useState(false);

  const onPageChange = (page) => setPage(page);

  useEffect(() => {
      if (headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight); // Update height when expanded
      }
  }, [expanded]);

  const getDatesToQuery = () => {
    let dates = []
    const startOfMonth = selectedMonth.clone().startOf('month');
    const endOfMonth = selectedMonth.clone().endOf('month');
    const minDate = moment.min(moment(), endOfMonth);

    let currDay = startOfMonth;
    while (currDay <= minDate) {
      dates.push(currDay.format('YYYY-MM-DD'));
      currDay.add(1, 'days')
      
    }
    return dates;
  }

  const handleRedirectToPlayer = (data) => async () => {
    if (data.player_id) {
      dispatch(setPlayerFilter({ player: data.player_id }));
      dispatch(getPlayerGPSData());
    }
  };

  const fetchAllData = async () => {
    const dates_to_query = getDatesToQuery();
    if (!dates_to_query || dates_to_query.length === 0) return;

    // Prepare all thunk dispatches
    const allStatusPromises = dates_to_query.map(date => {
      const cellKeyObj = players.reduce((acc, player) => {
        acc[`${player.player_id}_${date}Cell`] = true;
        return acc;
      }, {});
      return dispatch(getStatusTrackerData(cellKeyObj, date));
    });

    // Execute all in parallel
    await Promise.all(allStatusPromises);

    // Fetch available statuses (can run in parallel too, if needed)
    dispatch(getAvailableStatuses(dates_to_query[0], dates_to_query[dates_to_query.length - 1]));
  };

  useEffect(() => {
    getDatesHeaders();
    fetchAllData();
  }, [selectedMonth]);

  const formattedColumnHeader = (dt) => (
    <>
    <div style={{textAlign: 'center', padding: "10px"}}>
      <span style={{fontSize: 20}}> {dt.format('MMM')} </span>
      <span style={{fontSize: 30, lineHeight: '30px'}}> {dt.format('DD')} </span>
    </div>
    </>
  )

  const getDatesHeaders = () => {
    const daysInMonth = selectedMonth?.daysInMonth();  
  
    const dates = Array.from({ length: daysInMonth }).reduce((acc, _, i) => {
      const date = selectedMonth?.date(i + 1);
      acc[date.format("YYYY-MM-DD")] = formattedColumnHeader(date);
      return acc;
    }, {});
    
    setDateHeaders(dates);
  };

  useEffect(() => {
    let trackerDS = [], uKeys = [];
      for (let i = 0 ; i < players.length; i++) {
        let selectPlayerGroups = [];
        if (playerGroupsFilter.length > 0) {
          selectPlayerGroups = playerGroups.filter(pg => playerGroupsFilter.includes(pg.player_group_id))
        }

        let playersWithFilteredStatuses = new Set();
        if (playerStatuesFilter.length > 0) {
          const selectPlayerStatuses = statuses.filter(s => playerStatuesFilter.includes(s.id));
          for (let uK in trackerData) {
            const [playerId, dtStr] = uK.split("_");
            if (selectPlayerStatuses.some(ps => ps.name === trackerData[uK]?.status?.name)) {
              playersWithFilteredStatuses.add(Number(playerId));
            }
          }
        }
        
        if (
            (playersFilter.length > 0 && !playersFilter.includes(players[i]?.player_id)) ||
            (positionsFilter.length > 0 && !positionsFilter.includes(players[i]?.position)) ||
            (playerGroupsFilter.length > 0 && selectPlayerGroups.length > 0 && !selectPlayerGroups.some(pg => pg.player_ids.includes(players[i]?.player_id))) ||
            (playerStatuesFilter.length > 0 && playersWithFilteredStatuses.size > 0 && !playersWithFilteredStatuses.has(players[i]?.player_id))
        ) {
          continue;
        };

        let tableRow = {
          player: players[i],
        };

        for (const dt in dateHeaders) {
          const uKey =  `${players[i]?.player_id}_${dt}`;
          tableRow[String(dt)] = uKey;
          uKeys.push(uKey);
        }
        trackerDS.push(tableRow);
      }
      setUniqueKeys([...uKeys ]);
      setTotalRecords(trackerDS.length);
      setTrackerDataSource(paginatedData(trackerDS));
      
  }, [playersFilter, positionsFilter, playerGroupsFilter, playerStatuesFilter])


  const paginatedData = (ds) => {
    return ds.slice((page - 1) * pageSize, page * pageSize)
  };

  useEffect(() => {
    if (!dateHeaders || !players || Object.keys(dateHeaders).length === 0 || players.length === 0) return;
    const cols = [
      {
        title: <PlayerHeader filteredTrackerData={trackerDataSource} uniqueKeys={uniqueKeys}/>,
        dataIndex: 'player',
        key: 'player',
        fixed: 'left',
        width: 320,
        render: (player, record) => {

          return (
                  <Link
                    style={{ color: "black" }}
                    // className="nav-link"
                    to={{
                      pathname: "/playerdata",
                    }}
                    onClick={handleRedirectToPlayer(player)}
                  >
                    <PlayerCard player={player} key={`${player.player_id}_card`} uniqueKeys={uniqueKeys}/>
                  </Link>  
                );
        },
      },
    ];

    for(const dt in dateHeaders) {
      cols.push(
        {
          title: dateHeaders[dt], // "Feb 01"
          dataIndex: dt,
          key: dt,    // "2024-02-01"
          width: 70,
          height: 80,
          render: (uniqueKey, record) => {
            return (
              <StatusTrackerCell 
                key={uniqueKey}
                uniqueKey={uniqueKey}
                dateHeaders={dateHeaders}
              />
            );
          },
        }
      )
    }
    setColumns(cols);
  }, [dateHeaders, players, trackerDataSource])
  
  useEffect(() => {
      if (!dateHeaders || !players || Object.keys(dateHeaders).length === 0 || players.length === 0) return;
      let trackerDS = [], uKeys = [];
      for (let i = 0 ; i < players.length; i++) {
        let tableRow = {
          player: players[i],
        };

        for (const dt in dateHeaders) {
          const uKey = `${players[i]?.player_id}_${dt}`
          tableRow[String(dt)] = uKey;
          uKeys.push(uKey);
        }
        
        trackerDS.push(tableRow);
      }
      setUniqueKeys([...uKeys ]);
      setTotalRecords(trackerDS.length);
      setTrackerDataSource(paginatedData(trackerDS));
  }, [dateHeaders, players, page, pageSize])

  const onChangePlayer = (value) => {
    setExpanded(!expanded);
    dispatch(setFilter({ playersFilter: value }));
  };

  const onChangePosition = (value) => {
    setExpanded(!expanded);
    dispatch(setFilter({ positionsFilter: value }));
  };

  const setSelectedPlayerGroups = (value) => {
    setExpanded(!expanded);
    dispatch(setFilter({ playerGroupsFilter: value }));
  };

  const setSelectedPlayerStatuses = (value) => {
    setExpanded(!expanded);
    dispatch(setFilter({ playerStatuesFilter: value }));
  };

  const onMonthChange = (date, dateString) => {
    dispatch(setFilter({ selectedMonth: date }));
  };

  return (
    <>
      <Navbar />
      
      <Alert
      style={{width: '98%', margin: '10px', justifySelf: 'left'}}
      message={
      <span style={{float:'left', fontSize: '16px'}}><b>
        For a quick tour, <Typography.Link type="text" style={{marginLeft: '3px'}} onClick={() => setShowTutorial(true)}>Click here!</Typography.Link>
        </b>   
      </span>}
      type="info"
      closable
      showIcon
    />

      <HelpButton 
        page="Status Tracker" 
        extraContent={
        <Typography.Title level={5}>For a quick tour, 
          <Typography.Link type="text" style={{marginLeft: '3px'}} onClick={() => setShowTutorial(true)}>Click here!</Typography.Link>
        </Typography.Title>}
      />

      <StatusTrackerTutorial
        setOpen={setShowTutorial}
        isOpen={showTutorial}
      />

      <div className="tracker-container">
        <div className="tracker-header" ref={headerRef} >
          <div className="tracker-filter-container"  >
            <div>
              <DatePicker 
                value={selectedMonth}
                style={{ marginLeft: "10px" }}
                onChange={onMonthChange} 
                picker="month" 
                allowClear={false}
                format={"MMM YYYY"}
              />
            </div>
            <div>
              <Select
                allowClear
                showSearch
                style={{ width: 320, marginLeft: "10px" }}
                placeholder="Choose Players"
                value={playersFilter}
                onChange={onChangePlayer}
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                mode="multiple"
                maxTagCount={1}
              >
                {players !== null ? (
                  players.map((player, i) => {
                    return (
                      <Option
                        key={i}
                        value={player.player_id}
                      >
                        {`${formattedPlayerName(
                          teamName,
                          player.first_name +
                            " " +
                            player.last_name
                        )} #${player.number}`}
                      </Option>
                    );
                  })
                ) : (
                  <></>
                )}
              </Select>
            </div>
            <div>
              <Select
                allowClear
                showSearch
                style={{ width: 260, marginLeft: "10px" }}
                placeholder="Choose Positions"
                value={positionsFilter}
                onChange={onChangePosition}
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                mode="multiple"
                maxTagCount={3}
              >
                {positions !== null ? (
                  positions.map((pos, i) => {
                    return (
                      <Option
                        key={i}
                        value={pos}
                      >
                        {pos}
                      </Option>
                    );
                  })
                ) : (
                  <></>
                )}
              </Select>
            </div>
            <div>
              <Select
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder="Choose Player Groups"
                value={playerGroupsFilter}
                style={{ width: 250, marginLeft: "10px" }}
                onChange={setSelectedPlayerGroups}
                mode="multiple"
                maxTagCount={1}
              >
                {playerGroups.map((playerGroup, i) => (
                  <Option key={i} value={playerGroup?.player_group_id}>
                    {playerGroup?.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder="Choose Player Statuses"
                value={playerStatuesFilter}
                style={{ width: 305, marginLeft: "10px" }}
                onChange={setSelectedPlayerStatuses}
                mode="multiple"
                maxTagCount={2}
              >
                {statuses.map((status, i) => (
                  <Option key={i} value={status?.id}>
                    {status?.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          {
            statuses && statuses.length > 0 && (
              <div className="tracker-status-legend">
                {[...statuses, ...noDataStatusObj].map((status, i) => {
                  return (
                    <div 
                      className="tracker-status-legend-container"
                      key={`tracker-status-legend-${i}`}>
                      <div style={{ width: 20, height: 20, backgroundColor: status.color}}></div>
                      <div style={{marginLeft: 10}}>{status.name}</div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
        <div className="tracker-table-container" >
          <div style={{ marginTop: 16, marginBottom: 10,  textAlign: 'right' }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={Math.max(totalRecords, 1)} 
              onChange={onPageChange}
              hideOnSinglePage={false}
              showSizeChanger={false} 
            />
          </div>
          <Table 
            className="status-tracker-table"
            columns={columns} 
            dataSource={trackerDataSource} 
            bordered
            pagination={false}
            scroll={{ y: 550 }}
            sticky
            rowKey="id"
          />
        </div>
      </div>
    </>
  
  );
};
